<template>
  <v-row
    class="pa-2"
    no-gutters
    justify="end"
    align="center"
  >
    <slot/>
    <v-menu
      v-if="!isViewsSettingsHidden"
      offset-y
      disable-keys
      :close-on-content-click="false"
      min-width="500"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          v-bind="attrs"
          v-on="on"
          icon
          :loading="viewsLoading"
        >
          <v-badge
            left
            overlap
            v-if="activeViewKey"
            content="!"
          >
            <v-icon>mdi-cog-outline</v-icon>
          </v-badge>
          <v-icon v-else>mdi-cog-outline</v-icon>
        </v-btn>
      </template>
      <v-list>
      <v-overlay
        :absolute="true"
        :value="viewsLoading"
      >
        <v-progress-circular
          indeterminate
          color="primary"
          class="mb-0"
        />
      </v-overlay>
      <v-container>
        <v-row no-gutters justify="center">
          <v-btn
            small
            @click="changeToolbarViewsTab(null)"
          >
            {{ isViewsShown ? 'Cancel' : 'New View'}}
          </v-btn>
        </v-row>
      </v-container>
      <v-divider/>
      <v-container>
        <v-row v-if="!isViewsShown">
          <v-col>
            <v-list dense>
              <v-radio-group
                hide-details
                :value="activeViewKey"
                @change="changeActiveView"
              >
                <v-list-item dense>
                  <v-col cols="12">
                    <v-row dense>
                      <v-radio
                        class="mb-0"
                        label="Default View"
                        :value="0"
                      />
                      <sub class="grey--text">
                        ({{ headers.length }})
                      </sub>
                    </v-row>
                  </v-col>
                </v-list-item>
                <v-list-item dense>
                  <v-divider/>
                </v-list-item>
                <v-list-item
                  dense
                  v-for="(viewItem, viewIndex) in viewsList"
                  :key="viewIndex"
                >
                  <v-col cols="8">
                    <v-row dense>
                      <v-radio
                        class="mb-0"
                        :label="`${viewItem.name}`"
                        :value="viewItem.key"
                      />
                      <sub class="grey--text">
                        ({{ viewItem.columns.length }})
                      </sub>
                    </v-row>
                  </v-col>
                  <v-col cols="4">
                    <v-row dense justify="end">
                      <v-divider class="mx-2" vertical/>
                      <v-btn
                        small
                        icon
                        class="mx-2"
                        @click="changeToolbarViewsTab(viewItem)"
                      >
                        <v-icon>mdi-pencil</v-icon>
                      </v-btn>
                      <v-btn
                        small
                        icon
                        @click="removeStoredView(viewItem.key)"
                      >
                        <v-icon color="red">mdi-trash-can-outline</v-icon>
                      </v-btn>
                    </v-row>
                  </v-col>
                </v-list-item>
              </v-radio-group>
            </v-list>
          </v-col>
        </v-row>
        <v-row v-else>
          <v-col>
            <v-list-item>
              <v-text-field
                autofocus
                label="New view name"
                v-model="editViewName"
              />
            </v-list-item>
            <v-list-item>
              <v-tooltip :disabled="editViewColumns.length > 0" open-delay="250" top>
                <template v-slot:activator="{ on, attrs }">
                    <span
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-btn
                        small
                        color="primary"
                        :disabled="!editViewName || editViewColumns.length <= 0"
                        @click="saveEditView(false)"
                      >
                        Save
                      </v-btn>
                    </span>
                </template>
                <span>At least one column</span>
              </v-tooltip>
            </v-list-item>
            <v-list-item>
              <v-tooltip
                :disabled="editViewColumns.length > 0 || editViewKey === activeViewKey"
                open-delay="250"
                top
              >
                <template v-slot:activator="{ on, attrs }">
                    <span
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-btn
                        small
                        color="primary"
                        :disabled="!editViewName
                          || editViewKey === activeViewKey
                          || editViewColumns.length <= 0
                        "
                        @click="saveEditView(true)"
                      >
                        Save & active
                      </v-btn>
                    </span>
                </template>
                <span>At least one column</span>
              </v-tooltip>
            </v-list-item>
            <v-list-item dense>
              <v-divider/>
            </v-list-item>
            <v-list-item>
              <v-btn
                small
                @click="editViewColumns = []"
              >
                Clear all
              </v-btn>
            </v-list-item>
            <v-list-item>
              <v-btn
                small
                @click="setEditViewCols"
              >
                Choose all
              </v-btn>
            </v-list-item>
          </v-col>
          <v-col>
            <v-list max-height="600" class="overflow-y-auto">
              <v-list-item
                dense
                v-for="headerItem in headers"
                :key="headerItem.value"
              >
                <v-checkbox
                  dense
                  hide-details
                  :label="headerItem.text"
                  :input-value="editViewColumns.includes(headerItem.value)"
                  :disabled="preparedHeaders.length - 1 === 0 && preparedHeaders.includes(headerItem.value)"
                  @change="changeNewViewHeader($event, headerItem.value)"
                />
              </v-list-item>
            </v-list>
          </v-col>
        </v-row>
      </v-container>
    </v-list>
    </v-menu>
    <v-tooltip open-delay="250" v-if="filtersCount" top>
      <template v-slot:activator="{ on, attrs }">
        <span
          v-bind="attrs"
          v-on="on"
        >
          <v-btn
            class="mx-1"
            icon
            @click="$emit('reset-filters')"
          >
            <v-badge left overlap :content="filtersCount">
              <v-icon>mdi-filter-remove</v-icon>
            </v-badge>
          </v-btn>
        </span>
      </template>
      <span>Reset filters</span>
    </v-tooltip>
    <v-tooltip open-delay="250" top>
      <template v-slot:activator="{ on, attrs }">
        <span
          v-bind="attrs"
          v-on="on"
        >
          <v-btn
            class="mx-1"
            icon
            :loading="gridDataLoading"
            :disabled="gridDataLoading"
            @click="$emit('refresh')"
          >
            <v-icon>mdi-cached</v-icon>
          </v-btn>
        </span>
      </template>
      <span>Refresh</span>
    </v-tooltip>
  </v-row>
</template>

<script>
import randomatic from 'randomatic'

export default {
  name: 'table-toolbar',
  props: {
    isViewsSettingsHidden: {
      type: Boolean,
      default: false
    },
    gridDataLoading: {
      type: Boolean,
      default: false
    },
    viewsLoading: {
      type: Boolean,
      default: false
    },
    filtersCount: {
      type: Number,
      default: 0
    },
    headers: {
      type: Array,
      default: () => []
    },
    preparedHeaders: {
      type: Array,
      default: () => []
    },
    viewsList: {
      type: Array,
      default: () => []
    },
    activeViewKey: {
      type: [String, Number],
      default: ''
    }
  },
  data () {
    return {
      isViewsShown: false,
      editViewName: '',
      editViewColumns: [],
      editViewKey: ''
    }
  },
  methods: {
    saveEditView (isWithActive = false) {
      const editViewKey = this.editViewKey || randomatic('*', 20)
      this.$emit('save-edit-view', {
        name: this.editViewName,
        cols: this.editViewColumns,
        key: editViewKey,
        isWithActive
      })
      this.isViewsShown = false
    },
    changeNewViewHeader (value, header) {
      if (value) {
        // add
        this.editViewColumns.push(header)
      } else {
        // remove
        this.editViewColumns = this.editViewColumns.filter(preparedHeader => preparedHeader !== header)
      }
    },
    changeActiveView (e) {
      this.$emit('change-active-view', e)
    },
    changeToolbarViewsTab (view) {
      this.isViewsShown = !this.isViewsShown
      if (view) {
        this.editViewName = view.name
        this.editViewColumns = [...view.columns]
        this.editViewKey = view.key
      } else {
        this.editViewName = ''
        this.setEditViewCols()
        this.editViewKey = ''
      }
    },
    setEditViewCols () {
      this.editViewColumns = this.headers.map(header => header.value)
    },
    removeStoredView (viewItemKey) {
      // changeActiveView to default view
      if (viewItemKey === this.activeViewKey) this.changeActiveView(0)
      this.$emit('remove-view', viewItemKey)
    }
  }
}
</script>
